import React, {useState, useMemo} from 'react';
import _ from 'lodash';
import {useSnackbar} from 'notistack';
import {Container, MenuItem, Select, Stack, Typography} from '@mui/material';
import {useQuery} from '@tanstack/react-query';
import SCButton from '../../components/SCButton.js';
import SCTextField from '../../components/SCTextField.js';
import ConsentCheckbox from '../../components/ConsentCheckbox.js';
import {openLoginModal} from '../../components/Login.js';
import {useBusyCallback, useDebounce, useOnce, useQueryParam} from '../../util/react_hooks.js';
import {useUser} from '../../util/user.js';
import api from '../../util/api.js';

const OrderTypes = [
    {value: 'MONTH', label: `חודש ראשון ניסיון (20 ש"ח)`},
    {value: 'YEAR', label: `מנוי שנתי (350 ש"ח)`},
];

const useCouponCodeInfo = ({couponCode, orderType})=>{
    let couponCodeDebounced = useDebounce(couponCode, 1000);
    let {data, isLoading} = useQuery({
        queryKey: ['verify_coupon_code',
            {couponCode: couponCodeDebounced, orderType}],
        queryFn: ()=>api.tarotmail.verifyCouponCode(
            {couponCode: couponCodeDebounced, orderType}),
        enabled: !!(couponCodeDebounced?.length&&orderType),
    });
    return [data, isLoading];
};

export const Payform = props=>{
    let {noTrialOrder} = props; 
    let [consent, setConsent] = useState(false);
    let [orderType, setOrderType] = useState('YEAR');
    let [firstName, setFirstName] = useState('');
    let [lastName, setLastName] = useState('');
    let [email, setEmail] = useState('');
    let [phone, setPhone] = useState('');
    let [couponCode, setCouponCode] = useState('');
    let [qsOrderType, setQsOrderType] = useQueryParam('p_order_type');
    let [qsFirstName, setQsFirstName] = useQueryParam('p_first_name');
    let [qsLastName, setQsLastName] = useQueryParam('p_last_name');
    let [qsEmail, setQsEmail] = useQueryParam('p_email');
    let [qsPhone, setQsPhone] = useQueryParam('p_phone');
    useOnce(()=>{
        setOrderType(qsOrderType);
        setQsOrderType();
    }, !!qsOrderType);
    useOnce(()=>{
        setFirstName(qsFirstName);
        setQsFirstName();
    }, !!qsFirstName);
    useOnce(()=>{
        setLastName(qsLastName);
        setQsLastName();
    }, !!qsLastName);
    useOnce(()=>{
        setEmail(qsEmail);
        setQsEmail();
    }, !!qsEmail);
    useOnce(()=>{
        setPhone(qsPhone);
        setQsPhone();
    }, !!qsPhone);
    let [user] = useUser();
    let {enqueueSnackbar} = useSnackbar();
    useOnce(()=>setEmail(user.email), !!user);
    let [couponCodeInfo, couponCodeInfoLoading] = useCouponCodeInfo(
        {couponCode, orderType});
    let orderTypes = useMemo(()=>{
        if (!noTrialOrder)
            return OrderTypes;
        return OrderTypes.filter(type=>type.value!='MONTH');
    }, [noTrialOrder]);
    let [proceed, proceeding] = useBusyCallback(async _email=>{
        try {
            let res = await api.tarotmail.createPaypage({
                firstName,
                lastName,
                email: _email||email,
                country: 'IL',
                phone,
                consent,
                orderType,
                couponCode,
            });
            if (res.status=='user_exists')
            {
                return void openLoginModal({
                    note: 'כבר קיים משתמש עם המייל הזה',
                    onDone: user=>proceed(user.email),
                    email,
                });
            }
            window.location.href = res.paypage;
        } catch (e) {
            let errmsg = 'אירעה שגיאה, נסו שוב מאוחר יותר';
            if (e.code=='validation_error')
                errmsg = e.extra.map(c=>c.message).join('. ');
            else if (e.code&&e.message)
                errmsg = e.message;
            else
                console.error(e);
            enqueueSnackbar(errmsg, {variant: 'error'});
        }
    }, [enqueueSnackbar, firstName, lastName, email, phone, consent,
        orderType, couponCode]);
    let price = useMemo(()=>{
        let _price = {MONTH: 20, YEAR: 350}[orderType];
        if (couponCodeInfo?.amount)
            _price = couponCodeInfo.amount;
        return _price;
    }, [couponCodeInfo, orderType]);
    return <Container maxWidth="md">
      <Stack justifyContent="center" alignItems="center">
        <Stack maxWidth="400px" gap={2} padding={3} sx={{
            border: '1px solid #cccccc',
            borderRadius: '4px',
        }}>
          <Stack direction="column" gap={1} sx={{width: '100%'}}>
            <Typography color="text.secondary">סוג מנוי</Typography>
            <Select value={orderType} onChange={v=>setOrderType(v.target.value)}>
              {orderTypes.map(v=><MenuItem value={v.value}>{v.label}</MenuItem>)}
            </Select>
          </Stack>
          <SCTextField required label="שם פרטי" value={firstName} onChange={setFirstName}/>
          <SCTextField required label="שם משפחה" value={lastName} onChange={setLastName}/>
          <SCTextField required label={'דוא"ל'} value={email} onChange={setEmail}/>
          <SCTextField required label="טלפון" value={phone} onChange={setPhone}
            placeholder="לדוגמה: 0501234567"/>
          <Stack direction="column" gap={1} sx={{width: '100%'}}>
            <Typography color="text.secondary">מדינה</Typography>
            <Select value="IL">
              <MenuItem value="IL">ישראל</MenuItem>
            </Select>
          </Stack>
          <Stack direction="column">
            <SCTextField label="קוד קופון" value={couponCode} onChange={setCouponCode}/>
            {!!couponCode?.length&&<>
              {couponCodeInfo?.verified&&<Typography color="success.main">
                קוד קופון תקין
              </Typography>}
              {couponCodeInfo?.message&&<Typography color="error">
                {couponCodeInfo.message}
              </Typography>}
            </>}
          </Stack>
          <ConsentCheckbox value={consent} onChange={setConsent}/>
          <SCButton text={`מעבר לתשלום (${price} ש"ח)`} variant="contained"
            onClick={()=>proceed()} loading={proceeding}/>
        </Stack>
      </Stack>
    </Container>;
};
